export const histologyModule = {
  id: "histology",
  title: "Kidney Histology",
  sections: [
    // ── Cortex vs. Medulla ──────────────────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Cortex vs. Medulla — Overview</h3>
      <p>On histology, the kidney has two visually distinct zones: the <strong>cortex</strong> and the <strong>medulla</strong>.</p>
      <p>The <b class="purple-term">cortex</b> appears <strong>darker and more granular</strong> on H&E because it is packed with
      <strong>renal corpuscles</strong> (glomeruli + Bowman's capsules), <strong>proximal convoluted tubules (PCT)</strong>,
      and <strong>distal convoluted tubules (DCT)</strong>. The many mitochondria in the tubular cells contribute to
      their eosinophilic (pink) staining.</p>
      <p>The <b class="purple-term">medulla</b> appears <strong>paler and more striated</strong>. It contains the
      <strong>loops of Henle</strong> and <strong>collecting ducts</strong>, which run parallel to one another in
      bundles, giving the medulla its characteristic striped appearance. There are no glomeruli in the medulla.</p>
      <p>Columns of cortical tissue called <b class="purple-term">columns of Bertin</b> dip down between the
      medullary pyramids, which can occasionally be mistaken for a mass on imaging.</p>
      <div class="highlight-box">Cortex vs. Medulla at a glance:
        <ul>
          <li>Cortex: <em>darker, granular; contains renal corpuscles, PCT, DCT</em></li>
          <li>Medulla: <em>paler, striated; contains loops of Henle and collecting ducts — no glomeruli</em></li>
        </ul>
      </div>`,
      image: "/images/kidney/kidneyhistologyoverview.png",
    },
    {
      type: "question",
      text: "Which structures are found in the renal cortex but NOT in the medulla?",
      options: [
        "Collecting ducts and loops of Henle",
        "Renal corpuscles (glomeruli) and convoluted tubules",
        "Vasa recta and thin limbs of Henle",
        "Papillary ducts and minor calyces",
      ],
      correct: 1,
      explanation:
        "Renal corpuscles (glomeruli + Bowman's capsule) and convoluted tubules (PCT and DCT) are found exclusively in the cortex. The medulla contains the loops of Henle, collecting ducts, and vasa recta.",
    },
    {
      type: "question",
      text: "Why does the renal cortex appear darker and more granular on H&E compared to the medulla?",
      options: [
        "The cortex has more connective tissue",
        "The cortex is packed with glomeruli and tubular cells that are rich in mitochondria and stain eosinophilic",
        "The cortex has a greater blood supply causing more red cell staining",
        "The cortex contains more lymphatic vessels",
      ],
      correct: 1,
      explanation:
        "The cortex appears darker and granular on H&E because it is densely packed with renal corpuscles and tubular cells (especially PCT cells, which are rich in mitochondria and stain intensely eosinophilic). The medulla has tubules running parallel and a simpler cell profile, giving it a paler appearance.",
    },

    // ── Renal Corpuscle & Filtration Barrier ────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>The Renal Corpuscle</h3>
      <p>Each nephron begins with a <b class="purple-term">renal corpuscle</b>, which consists of two components:</p>
      <p>1. The <b class="purple-term">glomerulus</b>: a tuft of fenestrated capillaries where blood is filtered under pressure.</p>
      <p>2. <b class="purple-term">Bowman's capsule</b>: a cup-shaped epithelial structure that surrounds the glomerulus and collects the filtrate.
      It has two layers:
      <ul>
        <li><strong>Parietal layer</strong>: the outer wall, lined by simple squamous epithelium</li>
        <li><strong>Visceral layer</strong>: specialised cells called <b class="purple-term">podocytes</b>, which wrap around the glomerular capillaries with interdigitating foot processes (<em>pedicels</em>)</li>
      </ul>
      The space between the two layers is <b class="purple-term">Bowman's space</b>, where filtrate collects before entering the proximal tubule.</p>
      <p>Between the capillary loops sit <b class="purple-term">mesangial cells</b>. They provide structural support to the glomerular tuft and have contractile properties that help regulate glomerular blood flow. They also have phagocytic function, clearing immune complexes from the mesangium.</p>
      <div class="highlight-box">Renal corpuscle components:
        <ul>
          <li>Glomerulus: <em>fenestrated capillary tuft — site of filtration</em></li>
          <li>Bowman's capsule parietal layer: <em>simple squamous epithelium</em></li>
          <li>Bowman's capsule visceral layer: <em>podocytes with foot processes</em></li>
          <li>Bowman's space: <em>filtrate collects here</em></li>
          <li>Mesangial cells: <em>structural support, phagocytosis, regulate GFR</em></li>
        </ul>
      </div>`,
      image: "/images/kidney/renalcorpuscle.png",
    },
    {
      type: "text",
      title: "Histology",
      content: `<h3>The Glomerular Filtration Barrier</h3>
      <p>The <b class="purple-term">glomerular filtration barrier</b> is a three-layer structure that allows small
      molecules (water, ions, glucose) to pass freely while retaining large proteins and blood cells:</p>
      <p><strong>Layer 1 — Fenestrated endothelium:</strong> The glomerular capillaries are lined by
      <b class="purple-term">fenestrated endothelium</b> with large pores (70–100 nm). These pores
      allow free passage of water and small solutes but are too large to retain blood cells.</p>
      <p><strong>Layer 2 — Glomerular basement membrane (GBM):</strong> A thick basement membrane made of
      <strong>type IV collagen, laminin, and heparan sulphate</strong>. The heparan sulphate gives the GBM a
      <strong>strong negative charge</strong>, which repels negatively charged proteins like albumin. This is
      the main barrier to protein filtration.</p>
      <p><strong>Layer 3 — Podocyte foot processes:</strong> Podocytes extend interdigitating foot processes
      around the capillaries. The gaps between them (<b class="purple-term">filtration slits</b>) are bridged by
      the <b class="purple-term">slit diaphragm</b> (made of nephrin and podocin), which provides the final
      size-selective barrier.</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">Filtration barrier layers (outside → in)</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">Fenestrated endothelium</b> → blocks blood cells</li>
            <li><b class="purple-term">GBM</b> (type IV collagen + heparan sulphate) → negative charge blocks proteins</li>
            <li><b class="purple-term">Podocyte foot processes + slit diaphragm</b> (nephrin) → final size filter</li>
          </ul>
        </div>
      </div>`,
      image: "/images/kidney/filtrationbarrier.png",
    },
    {
      type: "question",
      text: "What gives the glomerular basement membrane (GBM) its charge selectivity?",
      options: [
        "Type IV collagen fibres",
        "Heparan sulphate proteoglycans",
        "Laminin cross-links",
        "Fenestrations in the endothelium",
      ],
      correct: 1,
      explanation:
        "Heparan sulphate proteoglycans in the GBM give it a strong negative charge, which repels negatively charged plasma proteins such as albumin. Loss of this charge selectivity (e.g. in minimal change disease) results in proteinuria despite normal glomerular structure on light microscopy.",
    },
    {
      type: "question",
      text: "What protein forms the slit diaphragm between podocyte foot processes?",
      options: [
        "Laminin",
        "Type IV collagen",
        "Nephrin",
        "Heparan sulphate",
      ],
      correct: 2,
      explanation:
        "Nephrin is the key structural protein of the slit diaphragm between podocyte foot processes. Mutations in the nephrin gene (NPHS1) cause congenital nephrotic syndrome of the Finnish type. Loss of nephrin results in foot process effacement and heavy proteinuria.",
    },
    {
      type: "question",
      text: "What is the function of mesangial cells in the glomerulus?",
      options: [
        "They produce renin in response to low blood pressure",
        "They provide structural support to the capillary tuft, regulate GFR, and phagocytose immune complexes",
        "They form the slit diaphragm between podocyte foot processes",
        "They produce the glomerular basement membrane",
      ],
      correct: 1,
      explanation:
        "Mesangial cells provide structural support to the glomerular capillary tuft, have contractile properties that help regulate GFR, and phagocytose immune complexes deposited in the mesangium. Mesangial expansion is an early feature of diabetic nephropathy.",
    },
    {
      type: "question",
      text: "What is Bowman's space?",
      options: [
        "The space inside the glomerular capillaries",
        "The space between the parietal and visceral layers of Bowman's capsule, where filtrate collects",
        "The gap between podocyte foot processes",
        "The lumen of the proximal tubule",
      ],
      correct: 1,
      explanation:
        "Bowman's space is the space between the parietal epithelium (outer layer) and the visceral epithelium (podocytes) of Bowman's capsule. Glomerular filtrate passes through the filtration barrier and collects in Bowman's space before draining into the proximal convoluted tubule.",
    },

    // ── PCT vs DCT ──────────────────────────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Proximal vs. Distal Convoluted Tubule</h3>
      <p>The proximal and distal convoluted tubules (PCT and DCT) are both found in the cortex and are lined by
      <strong>simple cuboidal epithelium</strong>, but they have distinct histological appearances that allow
      them to be distinguished.</p>
      <p>The <b class="purple-term">proximal convoluted tubule (PCT)</b>:</p>
      <ul>
        <li>Has a prominent <strong>brush border</strong> (dense microvilli on the apical surface), which gives the lumen a <strong>fuzzy, indistinct appearance</strong></li>
        <li>Cells are large and <strong>intensely eosinophilic</strong> (pink) due to abundant mitochondria</li>
        <li><strong>Fewer nuclei</strong> visible per cross-section because the cells are large</li>
        <li>Reabsorbs ~65% of filtered Na⁺, water, glucose, amino acids, HCO₃⁻, and phosphate</li>
      </ul>
      <p>The <b class="purple-term">distal convoluted tubule (DCT)</b>:</p>
      <ul>
        <li><strong>No brush border</strong> — the lumen appears <strong>clear and well-defined</strong></li>
        <li>Cells are <strong>smaller and paler</strong></li>
        <li><strong>More nuclei</strong> visible per cross-section (smaller cells, more of them fit around the lumen)</li>
        <li>Fine-tunes Na⁺, K⁺, and acid-base balance under hormonal control (aldosterone, PTH)</li>
      </ul>
      <div class="mnemonic-card">
        <div class="mnemonic-title">PCT vs DCT on histology</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">PCT</b>: <b>P</b>rominent brush border, <b>P</b>ink cytoplasm, <b>P</b>oorly defined lumen, <b>P</b>aucity of nuclei</li>
            <li><b class="purple-term">DCT</b>: <b>D</b>istinct lumen (no brush border), <b>D</b>uller (paler) cytoplasm, <b>D</b>ense nuclei</li>
          </ul>
        </div>
      </div>`,
      image: "/images/kidney/pctdct.png",
    },
    {
      type: "question",
      text: "On H&E histology, how do you distinguish the proximal convoluted tubule (PCT) from the distal convoluted tubule (DCT)?",
      options: [
        "The PCT has a clearly defined lumen; the DCT has a fuzzy lumen",
        "The PCT has a prominent brush border making the lumen look fuzzy; the DCT has no brush border and a clear lumen",
        "The PCT is found in the medulla; the DCT is in the cortex",
        "The PCT cells are smaller with more nuclei; the DCT cells are larger",
      ],
      correct: 1,
      explanation:
        "The PCT has a prominent brush border (dense microvilli) that makes its lumen appear fuzzy and indistinct. The DCT has no brush border, giving it a clear, well-defined lumen. The PCT cells are also larger and more eosinophilic than DCT cells.",
    },
    {
      type: "question",
      text: "Why do proximal convoluted tubule cells appear intensely eosinophilic (pink) on H&E?",
      options: [
        "They contain large amounts of glycogen",
        "They are rich in mitochondria, which stain eosinophilic",
        "They have a thick brush border that absorbs eosin",
        "They produce large amounts of renin",
      ],
      correct: 1,
      explanation:
        "PCT cells are rich in mitochondria to provide the ATP needed for the extensive active reabsorption they perform. Mitochondria stain eosinophilic (pink) with eosin, giving PCT cells their characteristic intensely pink appearance on H&E.",
    },
    {
      type: "question",
      text: "Why are more nuclei visible per cross-section in the DCT compared to the PCT?",
      options: [
        "DCT cells have multiple nuclei (binucleate cells)",
        "DCT cells are smaller so more cells are needed to line the same lumen, fitting more nuclei into view",
        "DCT nuclei are enlarged due to greater metabolic activity",
        "The DCT is larger in diameter, so more cells are needed",
      ],
      correct: 1,
      explanation:
        "DCT cells are smaller than PCT cells. Because more smaller cells are needed to surround the tubule lumen, more nuclei are visible per cross-section in the DCT compared to the PCT.",
    },

    // ── Loop of Henle & Collecting Duct ─────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Loop of Henle & Collecting Duct</h3>
      <p>The <b class="purple-term">loop of Henle</b> descends into the medulla and has two morphologically distinct limbs:</p>
      <p><strong>Thin descending limb and thin ascending limb:</strong> Lined by <strong>simple squamous epithelium</strong>, very similar in appearance to capillaries — the thin-walled cells are flat with a prominent nucleus. The thin descending limb is freely permeable to water but not to ions.</p>
      <p><strong>Thick ascending limb:</strong> Lined by <strong>simple cuboidal to low columnar epithelium</strong> with no brush border. This segment actively pumps Na⁺, K⁺, and Cl⁻ out of the lumen via the NKCC2 cotransporter — it is <strong>impermeable to water</strong>, which is essential for generating the medullary concentration gradient.</p>
      <p>The <b class="purple-term">collecting duct (CD)</b> runs through both the cortex and medulla and is one of the most recognisable structures on histology:</p>
      <ul>
        <li>Lined by <strong>simple cuboidal to columnar epithelium</strong></li>
        <li>Cells appear <strong>pale and clear</strong> with very <strong>distinct cell borders</strong> (like tiles or mosaic pieces) — this is the key identifying feature</li>
        <li>Contains two cell types:
          <ul>
            <li><b class="purple-term">Principal cells</b>: respond to <strong>ADH</strong> (insert aquaporin-2 channels for water reabsorption) and <strong>aldosterone</strong> (reabsorb Na⁺, secrete K⁺)</li>
            <li><b class="purple-term">Intercalated cells</b>: regulate acid-base balance (secrete H⁺ or HCO₃⁻)</li>
          </ul>
        </li>
      </ul>
      <div class="highlight-box">Identifying the collecting duct on histology:
        <ul>
          <li>Large, pale cells with <strong>very distinct cell borders</strong></li>
          <li>No brush border</li>
          <li>Found running parallel in the medulla (and cortex medullary rays)</li>
        </ul>
      </div>`,
      image: "/images/kidney/loopcd.png",
    },
    {
      type: "question",
      text: "Which segment of the nephron is impermeable to water and actively pumps Na⁺, K⁺, and Cl⁻ out of the lumen?",
      options: [
        "Thin descending limb of Henle",
        "Proximal convoluted tubule",
        "Thick ascending limb of Henle",
        "Principal cells of the collecting duct",
      ],
      correct: 2,
      explanation:
        "The thick ascending limb of the loop of Henle is impermeable to water and actively transports Na⁺, K⁺, and Cl⁻ out via the NKCC2 cotransporter. This dilutes the tubular fluid and generates the medullary concentration gradient. Loop diuretics (e.g. furosemide) work by blocking NKCC2.",
    },
    {
      type: "question",
      text: "What is the characteristic histological appearance of the collecting duct?",
      options: [
        "Fuzzy lumen with a prominent brush border",
        "Pale, clear cells with very distinct cell borders and no brush border",
        "Squamous cells with large flat nuclei",
        "Intensely eosinophilic cells with many mitochondria",
      ],
      correct: 1,
      explanation:
        "The collecting duct is identified on histology by its pale, clear cells with very distinct, sharply defined cell borders — sometimes described as looking like tiles or a mosaic. There is no brush border. This contrasts with the PCT (fuzzy lumen) and the DCT (clear lumen but less pale).",
    },
    {
      type: "question",
      text: "Which cell type in the collecting duct inserts aquaporin-2 channels in response to ADH?",
      options: [
        "Intercalated cells",
        "Mesangial cells",
        "Principal cells",
        "Podocytes",
      ],
      correct: 2,
      explanation:
        "Principal cells in the collecting duct insert aquaporin-2 (AQP2) water channels into their apical membrane in response to ADH (antidiuretic hormone, vasopressin), making the collecting duct permeable to water. Principal cells also respond to aldosterone, reabsorbing Na⁺ and secreting K⁺.",
    },

    // ── Juxtaglomerular Apparatus ────────────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>The Juxtaglomerular Apparatus (JGA)</h3>
      <p>The <b class="purple-term">juxtaglomerular apparatus (JGA)</b> is a specialised structure at the junction where the distal convoluted tubule returns to lie adjacent to its own glomerulus. It consists of three cell types:</p>
      <p>1. <b class="purple-term">Macula densa</b>: a plaque of tall, closely packed columnar cells in the wall of the DCT where it contacts the afferent arteriole. These cells act as <strong>salt sensors</strong> — they detect NaCl concentration in the tubular fluid and signal the JG cells to adjust renin secretion and GFR accordingly (tubuloglomerular feedback).</p>
      <p>2. <b class="purple-term">Juxtaglomerular (JG) cells</b>: modified smooth muscle cells in the wall of the <strong>afferent arteriole</strong>. They contain <strong>renin-filled granules</strong> and act as <strong>pressure sensors</strong> (baroreceptors). When afferent arteriole pressure falls, they release renin → activating the RAAS.</p>
      <p>3. <b class="purple-term">Extraglomerular mesangial cells (Lacis cells)</b>: situated between the macula densa and the JG cells. They relay signals between the two and are thought to play a modulating role.</p>
      <div class="mnemonic-card">
        <div class="mnemonic-title">JGA mnemonic: "MJ Lacis"</div>
        <div class="mnemonic-content">
          <ul>
            <li><b class="purple-term">M</b>acula densa — DCT salt sensor → controls renin release via TGF</li>
            <li><b class="purple-term">J</b>G cells — afferent arteriole pressure sensor → contain renin granules</li>
            <li><b class="purple-term">Lacis</b> cells (extraglomerular mesangial) — relay/modulate</li>
          </ul>
        </div>
      </div>`,
      image: "/images/kidney/jga.png",
    },
    {
      type: "question",
      text: "What do juxtaglomerular (JG) cells secrete and where are they located?",
      options: [
        "Aldosterone; in the adrenal cortex",
        "Renin; in the wall of the afferent arteriole",
        "Erythropoietin; in the peritubular capillary endothelium",
        "ADH; in the macula densa",
      ],
      correct: 1,
      explanation:
        "Juxtaglomerular (JG) cells are modified smooth muscle cells in the wall of the afferent arteriole. They contain renin-filled secretory granules and act as baroreceptors, releasing renin when afferent arteriole pressure drops (e.g. in hypotension or renal artery stenosis), thereby activating the RAAS.",
    },
    {
      type: "question",
      text: "What does the macula densa detect and what is its response?",
      options: [
        "It detects blood pressure changes and releases erythropoietin",
        "It detects NaCl concentration in the tubular fluid and signals JG cells to adjust renin secretion and GFR",
        "It detects blood oxygen levels and signals the bone marrow",
        "It detects ADH levels and adjusts aquaporin expression",
      ],
      correct: 1,
      explanation:
        "The macula densa is a group of tall, closely packed DCT cells that sense NaCl concentration in the tubular fluid. When NaCl is low (suggesting low GFR or low blood pressure), the macula densa signals JG cells to release more renin and causes afferent arteriole dilation to increase GFR. This is called tubuloglomerular feedback.",
    },
    {
      type: "question",
      text: "A patient has renal artery stenosis causing low perfusion pressure in the afferent arteriole. Which cells directly sense this and respond by releasing renin?",
      options: [
        "Macula densa cells",
        "Extraglomerular mesangial (Lacis) cells",
        "Juxtaglomerular (JG) cells in the afferent arteriole wall",
        "Podocytes of the glomerular visceral layer",
      ],
      correct: 2,
      explanation:
        "Juxtaglomerular (JG) cells are modified smooth muscle cells in the afferent arteriole wall that act as baroreceptors. When perfusion pressure falls (e.g. in renal artery stenosis), they directly sense the reduced stretch and respond by releasing renin, activating the RAAS to raise blood pressure.",
    },

    // ── Clinical histology correlations ─────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Clinical Histology: Glomerular Disease</h3>
      <p>Glomerular diseases are classified as either <strong>nephrotic</strong> (protein loss, oedema) or <strong>nephritic</strong> (haematuria, hypertension, oliguria). Their histological appearances are distinctive:</p>
      <p><b class="purple-term">Minimal change disease (MCD)</b>: Most common cause of nephrotic syndrome in <strong>children</strong>. Light microscopy is <strong>completely normal</strong> — hence the name. On <strong>electron microscopy</strong>, the podocyte foot processes are <b>effaced (fused)</b>. Responds well to steroids.</p>
      <p><b class="purple-term">Membranous nephropathy</b>: Most common cause of nephrotic syndrome in <strong>adults</strong>. Histology shows <strong>diffuse thickening of the GBM</strong>. On silver stain, a classic <strong>"spike and dome"</strong> pattern is seen — spikes of GBM material projecting between subepithelial immune deposits.</p>
      <p><b class="purple-term">Focal segmental glomerulosclerosis (FSGS)</b>: <strong>Focal</strong> (only some glomeruli affected) and <strong>segmental</strong> (only part of each affected glomerulus) sclerosis and hyalinosis. Foot process effacement on EM. Most common glomerular cause of nephrotic syndrome in <strong>adults in the USA</strong>.</p>
      <p><b class="purple-term">IgA nephropathy (Berger's disease)</b>: Most common <strong>glomerulonephritis worldwide</strong>. Presents with episodic haematuria following upper respiratory infections. Histology shows <strong>mesangial IgA deposits</strong> on immunofluorescence and mesangial hypercellularity on light microscopy.</p>
      <p><b class="purple-term">Goodpasture syndrome</b>: Anti-GBM antibodies attack both the GBM and pulmonary alveolar basement membrane. Immunofluorescence shows <strong>linear IgG deposits</strong> along the GBM — a hallmark finding.</p>
      <p><b class="purple-term">Diabetic nephropathy</b>: The most common cause of end-stage renal disease (ESRD) in developed countries. Histology shows <strong>GBM thickening</strong> and <strong>mesangial expansion</strong>. The classic lesion is <b class="purple-term">Kimmelstiel-Wilson nodules</b> — nodular deposits of hyaline material in the mesangium (nodular glomerulosclerosis).</p>
      <div class="highlight-box">Key glomerular diseases summary:
        <ul>
          <li>MCD: <em>normal LM + foot process effacement on EM — children — nephrotic</em></li>
          <li>Membranous: <em>GBM thickening + "spike and dome" — adults — nephrotic</em></li>
          <li>FSGS: <em>focal/segmental sclerosis — adults — nephrotic</em></li>
          <li>IgA nephropathy: <em>mesangial IgA — nephritic — post-URI haematuria</em></li>
          <li>Goodpasture: <em>linear IgG on GBM — nephritic + pulmonary haemorrhage</em></li>
          <li>Diabetic nephropathy: <em>GBM thickening + Kimmelstiel-Wilson nodules</em></li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "A 5-year-old presents with nephrotic syndrome. Renal biopsy shows a normal appearance on light microscopy. What will electron microscopy show, and what is the diagnosis?",
      options: [
        "Linear IgG deposits along the GBM — Goodpasture syndrome",
        "Effacement of podocyte foot processes — Minimal change disease",
        "Nodular mesangial deposits — Diabetic nephropathy",
        "Subepithelial spike and dome pattern — Membranous nephropathy",
      ],
      correct: 1,
      explanation:
        "Minimal change disease (MCD) shows a completely normal appearance on light microscopy. Electron microscopy reveals effacement (fusion) of podocyte foot processes. It is the most common cause of nephrotic syndrome in children and responds well to steroid treatment.",
    },
    {
      type: "question",
      text: "What is the classic histological finding of diabetic nephropathy on renal biopsy?",
      options: [
        "Linear IgG deposits along the GBM",
        "Mesangial IgA deposits on immunofluorescence",
        "Kimmelstiel-Wilson nodules — nodular mesangial sclerosis",
        "Normal light microscopy with foot process effacement on EM",
      ],
      correct: 2,
      explanation:
        "Kimmelstiel-Wilson nodules are the classic histological lesion of diabetic nephropathy — nodular deposits of hyaline material in the glomerular mesangium (nodular glomerulosclerosis). Other early changes include GBM thickening and diffuse mesangial expansion. Diabetic nephropathy is the most common cause of ESRD in developed countries.",
    },
    {
      type: "question",
      text: "A patient presents with haematuria and hypertension 2 weeks after a sore throat. Renal biopsy shows subepithelial immune deposits and hypercellular glomeruli. What does immunofluorescence show, and what is the diagnosis?",
      options: [
        "Linear IgG — Goodpasture syndrome",
        "Mesangial IgA — IgA nephropathy (Berger's disease)",
        "Granular IgG and C3 — Post-streptococcal glomerulonephritis",
        "No deposits — Minimal change disease",
      ],
      correct: 2,
      explanation:
        "Post-streptococcal glomerulonephritis (PSGN) presents 1–3 weeks after a streptococcal infection. Biopsy shows hypercellular glomeruli with subepithelial deposits giving a 'lumpy bumpy' pattern on IF (granular IgG and C3). It is a nephritic syndrome causing haematuria, hypertension, and oliguria.",
    },
    {
      type: "question",
      text: "What is the distinguishing immunofluorescence pattern in Goodpasture syndrome?",
      options: [
        "Granular IgA deposits in the mesangium",
        "Granular IgG and C3 along the GBM",
        "Linear IgG deposits along the GBM",
        "No immune deposits",
      ],
      correct: 2,
      explanation:
        "Goodpasture syndrome is caused by antibodies against type IV collagen in the GBM (anti-GBM antibodies). Because the antibodies bind uniformly along the entire GBM, immunofluorescence shows a characteristic linear IgG pattern. This contrasts with the granular pattern seen in immune complex diseases.",
    },

    // ── Tubular pathology ────────────────────────────────────────────────────
    {
      type: "text",
      title: "Histology",
      content: `<h3>Tubular Pathology: Acute Tubular Necrosis</h3>
      <p><b class="purple-term">Acute tubular necrosis (ATN)</b> is the most common cause of
      <strong>acute kidney injury (AKI)</strong> in hospitalised patients. It results from ischaemic or
      nephrotoxic injury to the tubular epithelial cells.</p>
      <p><strong>Histological findings:</strong></p>
      <ul>
        <li><strong>Loss of brush border</strong> in PCT cells — this is the earliest and most sensitive sign</li>
        <li><strong>Necrosis and sloughing</strong> of tubular epithelial cells — naked, denuded basement membrane</li>
        <li><strong>Cell debris and casts</strong> in the tubular lumen</li>
        <li><strong>Flattened epithelial cells</strong> attempting to regenerate</li>
        <li>Interstitial oedema</li>
      </ul>
      <p><strong>The medulla is particularly vulnerable</strong> to ischaemic ATN (as covered in anatomy) because the vasa recta supply deoxygenated blood that has already passed through the glomerulus. The <strong>straight portion of the PCT (S3 segment)</strong> in the outer medulla and the <strong>thick ascending limb</strong> are the most susceptible segments.</p>
      <p>ATN is usually <strong>reversible</strong> — tubular cells can regenerate from surviving cells. Recovery follows a polyuric phase as new cells reestablish but before they fully concentrate urine.</p>
      <div class="highlight-box">ATN histology key findings:
        <ul>
          <li>Loss of PCT brush border (earliest sign)</li>
          <li>Tubular cell necrosis with denuded basement membrane</li>
          <li>Cell debris and granular casts in the lumen</li>
          <li>Most vulnerable: PCT S3 segment and thick ascending limb (outer medulla)</li>
        </ul>
      </div>`,
    },
    {
      type: "question",
      text: "What is the most common cause of acute kidney injury (AKI) in hospitalised patients?",
      options: [
        "IgA nephropathy",
        "Minimal change disease",
        "Acute tubular necrosis (ATN)",
        "Membranous nephropathy",
      ],
      correct: 2,
      explanation:
        "Acute tubular necrosis (ATN) is the most common cause of AKI in hospitalised patients. It can be caused by ischaemia (e.g. hypotension, sepsis) or nephrotoxins (e.g. aminoglycosides, contrast dye, myoglobin from rhabdomyolysis). It is usually reversible if the cause is removed promptly.",
    },
    {
      type: "question",
      text: "What is the earliest histological sign of acute tubular necrosis (ATN)?",
      options: [
        "Kimmelstiel-Wilson nodule formation",
        "Loss of the brush border of PCT cells",
        "Linear IgG deposits along the tubular basement membrane",
        "Foot process effacement of podocytes",
      ],
      correct: 1,
      explanation:
        "The earliest histological sign of ATN is loss of the brush border (microvilli) in PCT cells. Subsequent findings include tubular cell necrosis, sloughing of cells leaving a bare basement membrane, and granular casts in the tubular lumen. These changes are most prominent in the PCT S3 segment and the thick ascending limb in the outer medulla.",
    },
  ],
};
